import React from 'react';

import styled from '@emotion/styled';
import { Link } from 'react-router-dom';

import { Asset } from '../Asset';
import { formatDate } from '../../services/formatDate';

import { media } from '../../services/media';
import { useStoreData } from '../../hooks';

interface GridProps {
    gap: string;
    gridColumnsMobile: string;
    gridColumnsTablet: string;
    gridColumnsDesktop: string;
}

interface ArticlesProps extends GridProps {
    limit: number;
    readMoreLabel: string;
    renderComponents: [
        'heading' | 'featuredImage' | 'excerpt' | 'readMoreButton' | 'date',
    ];
}

export const Articles: React.FC<ArticlesProps> = ({
    limit,
    readMoreLabel,
    renderComponents,
    ...props
}) => {
    const articles = useStoreData().storyblok.ArticleItems.items;

    if (!articles || !articles.length) {
        return <div>No published articles were found.</div>;
    }

    if (!renderComponents) {
        return null;
    }

    /** TODO: Add types */
    return (
        <Grid {...props}>
            {articles &&
                articles
                    .slice(0, limit ? limit : articles.length)
                    .map(({ content, slug, id, first_published_at }: any) => {
                        const { title, excerpt, featuredImage } = content;
                        return (
                            <article key={id}>
                                {title && renderComponents.includes('heading') && (
                                    <Heading>
                                        <Link to={`/blog/${slug}`}>
                                            {title}
                                        </Link>
                                    </Heading>
                                )}
                                {featuredImage &&
                                    renderComponents.includes(
                                        'featuredImage',
                                    ) && (
                                        <FeaturedImage
                                            asset={featuredImage}
                                            objectFit="cover"
                                        />
                                    )}
                                {excerpt &&
                                    renderComponents.includes('excerpt') && (
                                        <Excerpt>{excerpt}</Excerpt>
                                    )}
                                {renderComponents.includes('date') && (
                                    <Date>
                                        {formatDate(first_published_at)}
                                    </Date>
                                )}
                                {readMoreLabel &&
                                    renderComponents.includes(
                                        'readMoreButton',
                                    ) && (
                                        <ReadMoreButton to={`/blog/${slug}`}>
                                            {readMoreLabel}
                                        </ReadMoreButton>
                                    )}
                            </article>
                        );
                    })}
        </Grid>
    );
};

const Grid = styled.div<GridProps>`
    display: grid;
    max-width: 100%;

    ${(props) => `
        gap: ${props.gap};

        ${media.max('md')} {
            grid-template-columns: ${props.gridColumnsMobile};
        }

        ${media.between('md', 'lg')} {
            grid-template-columns: ${props.gridColumnsTablet};
        }

        ${media.min('lg')} {
            grid-template-columns: ${props.gridColumnsDesktop};
        }
    
    `};
`;

const Heading = styled.h2`
    a {
        font-size: 0.8em;
        text-decoration: none;
    }
`;

const FeaturedImage = styled(Asset)`
    margin: 0 0 1em;

    img {
        object-fit: cover;
        width: 100%;
        display: block;
        max-width: 100%;
        height: 100%;
    }

    ${media.max('lg')} {
        height: 200px;
    }

    ${media.min('lg')} {
        height: 300px;
    }
`;

const Excerpt = styled.p`
    line-height: 1.25;
    font-size: 0.85em;
`;

const Date = styled.div`
    font-size: 0.7em;
    margin: 0 0 1em;
`;

const ReadMoreButton = styled(Link)`
    display: block;
    color: inherit;
    font-weight: bold;
    text-transform: uppercase;
    font-size: 0.7em;
`;
